import { FieldValues } from 'react-hook-form'
import { UiSchema } from '@rjsf/utils'
import omit from 'lodash/omit'

import { DropdownField } from '../components/markup/utils/components/material-forms/form-fields/dropdown/dropdown'
import { calculateAreaMeasurementsForMaterial } from '../imup/sagas/2D/createDrawableLocation'
import { FormProperties } from '../imup/types'
import { ActiveDrawable } from '../models/activeDrawable'
import { GeneralDrawableSettings } from '../models/activeDrawableSettings'
import { Region } from '../models/region'
import { DRAWABLE_SETTINGS_TYPES, DRAWABLE_TYPES } from '../shared/constants/drawable-types'
import { TOOLTIP_FOR_TWL_SIXTEEN_FIELD } from '../shared/constants/form-schema/general'
import { prepareValue } from '../shared/services/opening-form-services'
import { parseScaleFactorLabel } from '../utils/calculations/scaleConversion/scaleConversion'
import { calculateTotalArea } from '../utils/formSchema/formSchema-helper-functions'
import { convertFullNumberFractionToDecimal } from '../utils/formSchema/numberConversion'
import { normalizeIJoistBrandSettings } from './utils/iJoistBrandSettings'

const otherValue = 'other'

export const valuesPreparation = (formData: FieldValues) => {
    const settings = { ...formData }

    if (
        prepareValue(settings.roof_system_type) !== 'traditional joist' &&
        prepareValue(settings.roof_system_type) !== 'traditional sleeper joist'
    ) {
        settings.material = null
    }
    if (prepareValue(settings.roof_system_type) !== 'i-joist') {
        settings.ijoist_size = null
        settings.ijoist_serie = null
    }

    if (settings.dimension !== 'other') {
        settings.dimension_other_width = null
        settings.dimension_other_height = null
    }

    // clear fields that can't be selected when type is Rafter
    if (prepareValue(settings.type) !== DRAWABLE_SETTINGS_TYPES.RAFTER) {
        settings.rafter_tail = null
    }

    if (prepareValue(settings.brand).includes('tji')) {
        settings.bci_series = null
        settings.lpi_series = null
        settings.ajs_series = null
        settings.pwi_series = null
    }

    if (prepareValue(settings.brand).includes('bci')) {
        settings.tji_series = null
        settings.lpi_series = null
        settings.ajs_series = null
        settings.pwi_series = null
    }

    if (prepareValue(settings.brand).includes('lpi')) {
        settings.bci_series = null
        settings.tji_series = null
        settings.ajs_series = null
        settings.pwi_series = null
    }

    if (prepareValue(settings.brand).includes('pwi')) {
        settings.bci_series = null
        settings.tji_series = null
        settings.ajs_series = null
        settings.lpi_series = null
    }

    if (prepareValue(settings.brand).includes('ajs')) {
        settings.lpi_series = null
        settings.bci_series = null
        settings.tji_series = null
        settings.pwi_series = null
    }

    return settings
}

export const uiSchema: UiSchema = {
    'ui:order': [
        'type',
        'roof_system_type',
        'pitch',
        'pitch_other',
        'engineered',
        'material',
        'dimensional_joist_dimensions',
        'ewp_type',
        'ewp_joist_dimensions',
        'dimensional_other_thickness',
        'dimensional_other_width',
        'joist_thickness_other',
        'joist_width_other',
        'brand',
        'ajs_series',
        'bci_series',
        'lpi_series',
        'pwi_series',
        'tji_series',
        'ijoist_size',
        'ijoist_size_tji',
        'ijoist_size_ajs',
        'ijoist_size_lpi',
        'ijoist_size_bci',
        'ijoist_size_pwi',
        'oc_spacing',
        'oc_spacing_other',
        'direction',
        'dimension',
        'truss_size',
        'truss_direction',
        'ribbon',
        'ribbon_dimensions',
        'ribbon_other_thickness',
        'ribbon_other_width',
        'purlin',
        'purlin_dimensions',
        'purlin_thickness_other',
        'purlin_other_width',
        'purlin_oc_spacing',
        'purlin_oc_spacing_other',
        'rafter_tail',
        'is_treated',
        'treated',
        'floor_level_rafter',
    ],
    type: {
        'ui:placeholder': 'Select Framing Type',
    },
    truss_size: {
        'ui:placeholder': 'Select Size',
        'ui:field': 'SectionDivider',
        'ui:options': {
            label: 'Truss',
            topDividerFieldType: 'dropdown',
            symbol: '"',
        },
    },
    roof_system_type: {
        'ui:placeholder': 'Select Roof System Type',
    },
    material: {
        'ui:placeholder': 'Select Material',
        'ui:field': 'SectionDivider',
        'ui:options': {
            label: 'Joist',
            dropdownWithPlaceholder: true,
            topDividerFieldType: 'dropdown',
        },
    },
    oc_spacing: {
        'ui:field': DropdownField,
        'ui:placeholder': 'Select O.C Spacing',
        'ui:options': {
            symbol: '"',
        },
    },
    direction: {
        'ui:field': 'DropdownField',
        'ui:placeholder': 'Select Direction',
    },
    dimensional_joist_dimensions: {
        'ui:field': 'LumberDimensionsField',
    },
    floor_level_rafter: {
        'ui:title': 'Floor Level',
        'ui:placeholder': 'Select Floor Level',
        'ui:field': 'SectionDivider',
        'ui:options': {
            dropdownWithPlaceholder: true,
            topDividerFieldType: 'dropdown',
            label: 'Sub Region',
        },
    },
    ribbon_dimensions: {
        'ui:field': 'LumberDimensionsField',
        'ui:options': {
            tooltips: {
                length: {
                    text: TOOLTIP_FOR_TWL_SIXTEEN_FIELD,
                },
            },
        },
    },
    purlin_dimensions: {
        'ui:field': 'LumberDimensionsField',
        'ui:options': {
            tooltips: {
                length: {
                    text: TOOLTIP_FOR_TWL_SIXTEEN_FIELD,
                },
            },
        },
    },
    ewp_joist_dimensions: {
        'ui:field': 'LumberDimensionsField',
    },
    is_treated: {
        'ui:field': 'RadioButtons',
        'ui:options': {
            inline: true,
        },
    },
    ijoist_size: {
        'ui:field': DropdownField,
        'ui:placeholder': 'Select I-Joist Size',
        'ui:options': {
            symbol: '"',
        },
    },
    ijoist_size_tji: {
        'ui:field': DropdownField,
        'ui:placeholder': 'Select I-Joist Size',
        'ui:options': {
            symbol: '"',
        },
    },
    ijoist_size_ajs: {
        'ui:field': DropdownField,
        'ui:placeholder': 'Select I-Joist Size',
        'ui:options': {
            symbol: '"',
        },
    },
    ijoist_size_lpi: {
        'ui:field': DropdownField,
        'ui:placeholder': 'Select I-Joist Size',
        'ui:options': {
            symbol: '"',
        },
    },
    ijoist_size_bci: {
        'ui:field': DropdownField,
        'ui:placeholder': 'Select I-Joist Size',
        'ui:options': {
            symbol: '"',
        },
    },
    ijoist_size_pwi: {
        'ui:field': DropdownField,
        'ui:placeholder': 'Select I-Joist Size',
        'ui:options': {
            symbol: '"',
        },
    },
    ajs_series: {
        'ui:placeholder': 'Select Series',
    },
    bci_series: {
        'ui:placeholder': 'Select Series',
    },
    lpi_series: {
        'ui:placeholder': 'Select Series',
    },
    pwi_series: {
        'ui:placeholder': 'Select Series',
    },
    tji_series: {
        'ui:placeholder': 'Select Series',
    },
    pitch: {
        'ui:placeholder': 'Select Pitch',
    },
    rafter_tail: {
        'ui:placeholder': 'Select Rafter Tail',
    },
    engineered: {
        'ui:field': 'RadioButtons',
        'ui:options': {
            inline: true,
        },
    },
    ribbon: {
        'ui:field': 'SectionDivider',
        'ui:options': {
            topDividerFieldType: 'switch',
            isLabelBold: true,
        },
    },
    purlin: {
        'ui:field': 'SectionDivider',
        'ui:options': {
            topDividerFieldType: 'switch',
            isLabelBold: true,
        },
    },
    purlin_oc_spacing: {
        'ui:field': DropdownField,
        'ui:placeholder': 'Select O.C Spacing',
        'ui:options': {
            symbol: '"',
        },
    },
    brand: {
        'ui:placeholder': 'Select Brand',
        'ui:field': 'SectionDivider',
        'ui:options': {
            dropdownWithPlaceholder: true,
            topDividerFieldType: 'dropdown',
            label: 'I-Joist',
        },
    },
}

const onValidate = () => {}

const onSubmit = (settings, formData) => {
    const newSettings = {
        ...settings,
        ...omit(valuesPreparation(formData), ['quantity']),
    }

    if (newSettings.type === 'Rafter') {
        newSettings.floor_level = newSettings.floor_level_rafter
        newSettings.floor_level_rafter = null
    }

    if (newSettings.dimensional_joist_dimensions) {
        if (newSettings.dimensional_joist_dimensions.thickness === otherValue) {
            newSettings.dimensional_joist_dimensions.thickness = newSettings.dimensional_other_thickness
        }
        newSettings.dimensional_other_thickness = null

        if (newSettings.dimensional_joist_dimensions.width === otherValue) {
            newSettings.dimensional_joist_dimensions.width = newSettings.dimensional_other_width
        }
        newSettings.dimensional_other_width = null
    }

    if (newSettings.ewp_joist_dimensions) {
        if (newSettings.ewp_joist_dimensions.thickness === otherValue) {
            newSettings.ewp_joist_dimensions.thickness = convertFullNumberFractionToDecimal(
                newSettings.joist_thickness_other
            )
        }
        newSettings.joist_thickness_other = null

        if (newSettings.ewp_joist_dimensions.width === otherValue) {
            newSettings.ewp_joist_dimensions.width = convertFullNumberFractionToDecimal(newSettings.joist_width_other)
        }
        newSettings.joist_width_other = null
    }

    if (newSettings.ribbon_dimensions) {
        if (newSettings.ribbon_dimensions.thickness === otherValue) {
            newSettings.ribbon_dimensions.thickness = newSettings.ribbon_other_thickness
        }
        newSettings.ribbon_other_thickness = null

        if (newSettings.ribbon_dimensions.width === otherValue) {
            newSettings.ribbon_dimensions.width = newSettings.ribbon_other_width
        }
        newSettings.ribbon_other_width = null
    }

    if (newSettings.purlin_dimensions) {
        if (newSettings.purlin_dimensions.thickness === otherValue) {
            newSettings.purlin_dimensions.thickness = newSettings.purlin_thickness_other
        }
        newSettings.purlin_other_thickness = null

        if (newSettings.purlin_dimensions.width === otherValue) {
            newSettings.purlin_dimensions.width = newSettings.purlin_other_width
        }
        newSettings.purlin_other_width = null
    }

    newSettings.pitch = 'pitch' in newSettings ? newSettings.pitch : null

    if (newSettings.pitch === otherValue && newSettings.pitch_other) {
        newSettings.pitch = String(newSettings.pitch_other).toUpperCase()

        delete newSettings.pitch_other
    } else if (newSettings.pitch !== otherValue) {
        newSettings.pitch_other = null
    }

    if (newSettings.oc_spacing === otherValue && newSettings.oc_spacing_other) {
        newSettings.oc_spacing = newSettings.oc_spacing_other
        delete newSettings.oc_spacing_other
    } else if (newSettings.oc_spacing !== otherValue) {
        newSettings.oc_spacing_other = null
    }

    if (newSettings.purlin_oc_spacing === otherValue && newSettings.purlin_oc_spacing_other) {
        newSettings.purlin_oc_spacing = newSettings.purlin_oc_spacing_other
        delete newSettings.purlin_oc_spacing_other
    } else if (newSettings.purlin_oc_spacing !== otherValue) {
        newSettings.purlin_oc_spacing_other = null
    }

    if (
        newSettings.ewp_joist_dimensions &&
        newSettings.ewp_joist_dimensions.width != null &&
        newSettings.ewp_joist_dimensions != null
    ) {
        newSettings.joist_dimensions = newSettings.ewp_joist_dimensions
        delete newSettings.ewp_joist_dimensions
    }

    if (
        newSettings.dimensional_joist_dimensions &&
        newSettings.dimensional_joist_dimensions.width != null &&
        newSettings.dimensional_joist_dimensions != null
    ) {
        newSettings.joist_dimensions = newSettings.dimensional_joist_dimensions
        delete newSettings.dimensional_joist_dimensions
    }

    newSettings.series = null
    normalizeIJoistBrandSettings(newSettings)

    return newSettings
}

/**
 * Augment the drawables of the from based on the new settings
 * @returns
 * @param input
 */
function prepareDrawables(input: {
    drawables: ActiveDrawable[]
    newSettings: GeneralDrawableSettings
    scaleFactor: number
    dpi: number | null
    pdfScale: number
    xCalibrationFactor: number
    yCalibrationFactor: number
    regions: Region[]
    is3D?: boolean
}): ActiveDrawable[] {
    const { drawables, newSettings, scaleFactor, dpi, pdfScale, xCalibrationFactor, yCalibrationFactor, regions } =
        input

    return drawables.map((drawable) => {
        const totalArea: number = calculateTotalArea(
            drawable.opening_locations,
            regions,
            parseScaleFactorLabel(drawable.additional_data.scale_factor) ?? scaleFactor,
            pdfScale,
            xCalibrationFactor,
            yCalibrationFactor,
            dpi
        )

        drawable.settings.quantity = calculateAreaMeasurementsForMaterial(totalArea, {
            type: DRAWABLE_TYPES.ROOF_SYSTEMS,
            settings: newSettings,
        })

        return drawable
    })
}

const form: FormProperties = { uiSchema, onValidate, onSubmit, prepareDrawables }

export default form
